import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TableSkeletonRows, TableEmptyRow } from '@/components/ui/table-states';
import { Edit, Trash2, Users, MoreHorizontal } from 'lucide-react';
import { useClientsConfig } from '@/config/clients-config';
import { Client } from '@/lib/store';
import { ClientsTableProps } from './types';

export function ClientsTable({
  clients,
  loading = false,
  onEdit,
  onDelete,
  searchQuery = ''
}: ClientsTableProps) {
  const config = useClientsConfig();
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: string) => {
    if (!config.enableSorting) return;
    
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedClients = [...clients].sort((a, b) => {
    if (!sortField) return 0;
    
    const aValue = a[sortField as keyof Client]?.toString().toLowerCase() || '';
    const bValue = b[sortField as keyof Client]?.toString().toLowerCase() || '';
    
    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const filteredClients = sortedClients.filter((client) => {
    if (!searchQuery) return true;
    
    const searchTerm = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(searchTerm) ||
      client.friendly_name?.toLowerCase().includes(searchTerm) ||
      client.email?.toLowerCase().includes(searchTerm) ||
      client.address?.toLowerCase().includes(searchTerm) ||
      client.country?.toLowerCase().includes(searchTerm) ||
      client.vat_number?.toLowerCase().includes(searchTerm) ||
      client.vat_setting?.toLowerCase().includes(searchTerm) ||
      client.vat_override_message?.toLowerCase().includes(searchTerm) ||
      client.contacts?.some((contact) => 
        contact.name?.toLowerCase().includes(searchTerm) ||
        contact.email?.toLowerCase().includes(searchTerm)
      )
    );
  });

  const handleDeleteClick = (clientId: number, clientName: string) => {
    if (config.confirmDelete) {
      if (confirm(`Are you sure you want to delete "${clientName}"?`)) {
        onDelete(clientId);
      }
    } else {
      onDelete(clientId);
    }
  };

  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <TableHead
      className={config.enableSorting ? 'cursor-pointer hover:bg-muted/50 select-none' : ''}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {children}
        {config.enableSorting && sortField === field && (
          <span className="text-xs">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </TableHead>
  );

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableHeader field="name">
              Name
            </SortableHeader>
            <SortableHeader field="email">
              Email
            </SortableHeader>
            <TableHead className="hidden md:table-cell">
              Country
            </TableHead>
            {config.showVatDetails && (
              <TableHead className="hidden lg:table-cell">
                VAT Setting
              </TableHead>
            )}
            {config.showContactCount && (
              <TableHead className="hidden lg:table-cell">
                Contacts
              </TableHead>
            )}
            <TableHead className="w-16 text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableSkeletonRows colSpan={6} />
          ) : filteredClients.length === 0 ? (
            <TableEmptyRow colSpan={6}>
              {searchQuery
                ? 'No clients match your search.'
                : 'No clients yet. Add a client to get started.'}
            </TableEmptyRow>
          ) : (
            filteredClients.map((client) => (                <TableRow 
                  key={client.id}
                  className={config.enableAnimations ? 'transition-colors duration-150' : ''}
                >
                <TableCell className="font-medium">
                  <div>
                    <div className="font-semibold">
                      {config.showFriendlyName && client.friendly_name 
                        ? client.friendly_name 
                        : client.name}
                    </div>
                    {config.showFriendlyName && client.friendly_name && (
                      <div className="text-sm text-muted-foreground">
                        {client.name}
                      </div>
                    )}
                  </div>
                </TableCell>
                
                <TableCell>
                  <a
                    href={`mailto:${client.email}`}
                    className="text-foreground/80 hover:text-foreground hover:underline"
                  >
                    {client.email}
                  </a>
                </TableCell>
                
                <TableCell className="hidden md:table-cell whitespace-nowrap">
                  {client.country}
                </TableCell>
                
                {config.showVatDetails && (
                  <TableCell className="hidden lg:table-cell">
                    <StatusBadge 
                      status={client.vat_setting} 
                      type="vat-setting"
                    />
                  </TableCell>
                )}
                
                {config.showContactCount && (
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-1">
                      <span>{(client.contacts?.length || 0) + (client.client_contacts?.length || 0)}</span>
                      <Users className="h-3 w-3 text-muted-foreground" />
                    </div>
                  </TableCell>
                )}
                
                <TableCell className="text-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" title="Client actions">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(client)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit client
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleDeleteClick(client.id, client.name)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
